import {LogUploadStatus} from '@models/adminstration/gateway/gateway-configuration/log-upload-status';
import {RebootStatus} from '@models/adminstration/gateway/gateway-reboot-status';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {UPDATE_STATE} from '@models/gateway/update/update-state.constant';
import {REBOOT_STATE} from '@constants/administration/gateway/reboot-state';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {NotificationService} from '@services/notification/notification.service';
import {
  GatewayFunctionsModalComponent
} from '@components/administration/gateway/gateway-configuration/gateway-functions/gateway-functions-modal/gateway-functions-modal.component';
import {interval, Observable, Subject, Subscription} from 'rxjs';
import {UpdateStatus} from '@models/gateway/update/update-status';
import {LOG_UPLOAD_STATE} from '@constants/administration/gateway/log-upload-state';
import {switchMap, take, takeUntil} from 'rxjs/operators';
import {
  GatewayCommunicationModalComponent
} from '@components/administration/gateway/gateway-configuration/gateway-functions/gateway-communication-modal/gateway-communication-modal.component';
import {USER_ROLE} from '@constants/user-role.constant';
import {User} from '@models/user/user';
import {ModalService} from '@services/modal/modal.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {UtilsService} from '@services/utils/utils.service';

@Component({
    selector: 'scitis-admin-gateway-functions',
    templateUrl: './gateway-functions.component.html',
    styleUrls: ['./gateway-functions.component.scss'],
    standalone: false
})
export class GatewayFunctionsComponent implements OnInit, OnDestroy {

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  public isCPOneGateway: boolean = false;
  @Input()
  public isCPEdgeGateway: boolean = false;
  @Input()
  public isCPLightGateway: boolean = false;
  @Input()
  public isCPSoftGateway: boolean = false;
  @Input()
  public isGenericGateway: boolean = false;
  @Input()
  public isArgosGateway: boolean = false;
  @Input()
  public superOrTechAdminRole: boolean = false;

  @Input()
  public superAdminRole: boolean = false;

  public updateState: typeof UPDATE_STATE = UPDATE_STATE;
  public rebootState: typeof REBOOT_STATE = REBOOT_STATE;
  public logUploadState: typeof LOG_UPLOAD_STATE = LOG_UPLOAD_STATE;
  public gatewayCommandOrConfigurationSent: boolean = false;
  public userDetailsLoaded: boolean = false;

  private COMPONENT_NAME: string = 'GatewayFunctionsComponent';
  private POLL_TIMEOUT = 20000;
  private REQUEST_COUNT = 15;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private rootOrganizationId: number = -1;
  private rootMachineryGroupId: number = -1;
  private whitelistedMachineryGroups: number[] = [];


  constructor(private translate: TranslateService,
              private confirmationDialogService: ConfirmationDialogService,
              private loadingIndicator: LoadingIndicatorService,
              private gatewayService: GatewayService,
              private notification: NotificationService,
              private authenticationService: FirebaseAuthenticationService,
              private modalService: ModalService,
              private utilsService:UtilsService) {
  }

  ngOnInit() {
    this.handleCPOneStatuses();
    this.setLoggedUserDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onCPOneSoftwareUpdate() {
    if (!this.isCPOneGateway) {//available only for CPONE gateway
      return;
    }
    console.log(`${this.COMPONENT_NAME}. update Gateway: ${this.gateway.name}`);
    const updateMessageTitle = this.translate.instant('Update Gateway');
    const updateMessageBody = this.translate.instant('Do you really want to update the gateway %s?').replace('%s', this.gateway.name.toBold());
    const updateMessageOK = this.translate.instant('Update Gateway');

    this.confirmationDialogService.handleConfirmationDialog(updateMessageTitle, updateMessageBody, '', updateMessageOK).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.loadingIndicator.show(this.translate.instant('Updating'));
        this.gatewayService.updateGateway(this.gateway, 'notimplemented').subscribe(() => {
          if (!this.gateway.updateStatus) {
            this.gateway.updateStatus = {
              state: UPDATE_STATE.PENDING,
              nextUpdate: Date.now(),
              lastUpdate: Date.now()
            };
          } else {
            this.gateway.updateStatus.state = UPDATE_STATE.PENDING;
          }

          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('Gateway update started'), this.COMPONENT_NAME, false);
          this.updateStatus();
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Gateway update failed'), error, this.COMPONENT_NAME, true);
          this.updateStatus();
        });
      }
    }, (_error) => {
      this.loadingIndicator.hide();
    });
  }

  onCPOneConfigurationUpload() {
    if (!this.isCPOneGateway) {//available only for CPONE gateway
      return;
    }
    console.log(`${this.COMPONENT_NAME}. upload config for gateway: ${this.gateway.name}`);
    const uploadMessageTitle = this.translate.instant('Upload configuration');
    const uploadMessageBody = this.translate.instant('Do you really want to upload configuration from %s?').replace('%s', this.gateway.name.toBold());
    const uploadMessageOK = this.translate.instant('Upload');

    this.confirmationDialogService.handleConfirmationDialog(uploadMessageTitle, uploadMessageBody, '', uploadMessageOK).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.loadingIndicator.show(this.translate.instant('upload gateway configuration'));
        this.gateway.configUploadRequested = true;
        this.gateway.configDownloadRequested = false;
        this.gatewayService.saveGateway(this.gateway).subscribe(() => {
          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('Gateway configuration upload started'), this.COMPONENT_NAME, false);
          this.uploadConfigStatus();
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Gateway configuration upload failed'), error, this.COMPONENT_NAME, true);
        });
      }
    }, (error) => {
      this.loadingIndicator.hide();
      this.notification.error(this.translate.instant('Gateway configuration upload request failed'), error, this.COMPONENT_NAME, true);
    });
  }

  onCPOneConfigurationDownload() {
    if (!this.isCPOneGateway) {//available only for CPONE gateway
      return;
    }
    const modalRef = this.modalService.open(GatewayFunctionsModalComponent, {size: 'lg'});
    modalRef.componentInstance.gateway = this.gateway;
    modalRef.componentInstance.okButtonText = this.translate.instant('Download');
    modalRef.componentInstance.rootOrganizationId = this.rootOrganizationId;
    modalRef.componentInstance.rootMachineryGroupId = this.rootMachineryGroupId;
    modalRef.componentInstance.whitelistedMachineryGroups = this.whitelistedMachineryGroups;
    modalRef.componentInstance.isSuperAdmin = this.superAdminRole;

    modalRef.result.then((result) => {
      console.log(`Closed with: ${result as Gateway}`);
      this.loadingIndicator.show(this.translate.instant('download gateway configuration'));
      this.gatewayService.saveGateway(result).subscribe(() => {
        this.loadingIndicator.hide();
        this.notification.success(this.translate.instant('Gateway configuration download started'), this.COMPONENT_NAME, false);
        this.downloadConfigStatus();
      }, (error) => {
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Gateway configuration download failed'), error, this.COMPONENT_NAME, true);
      });
    });
  }

  onCPOneReboot() {
    if (!this.isCPOneGateway) {//available only for CPONE gateway
      return;
    }
    console.log(`${this.COMPONENT_NAME}. reboot Gateway: ${this.gateway.name}`);
    const rebootMessageTitle = this.translate.instant('Reboot Gateway');
    const rebootMessageBody = this.translate.instant('Do you really want to reboot the gateway %s?').replace('%s', this.gateway.name.toBold());
    const rebootMessageOK = this.translate.instant('Reboot Gateway');

    this.confirmationDialogService.handleConfirmationDialog(rebootMessageTitle, rebootMessageBody, '', rebootMessageOK).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.gateway.rebootRequest = true;
        this.gatewayService.saveGateway(this.gateway).subscribe(() => {
          this.notification.success(this.translate.instant('Gateway reboot started'), this.COMPONENT_NAME, false);
          this.rebootStatus();
        }, (error) => {
          this.notification.error(this.translate.instant('Gateway reboot failed'), error, this.COMPONENT_NAME, true);
          this.rebootStatus();
        });
      }
    }, (error) => {
      this.notification.error(this.translate.instant('Gateway reboot request failed'), error, this.COMPONENT_NAME, true);
    });
  }

  onCPOneLogRequest() {
    if (!this.isCPOneGateway) {//available only for CPONE gateway
      return;
    }
    console.log(`${this.COMPONENT_NAME}. Gateway log request: ${this.gateway.name}`);
    const logRequestMessageTitle = this.translate.instant('Request Logs');
    const logRequestMessageBody = this.translate.instant('Start retrieving logs for the gateway %s?').replace('%s', this.gateway.name.toBold());
    const logRequestMessageOK = this.translate.instant('Request Logs');

    this.confirmationDialogService.handleConfirmationDialog(logRequestMessageTitle, logRequestMessageBody, '', logRequestMessageOK).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.gateway.logUploadRequested = true;
        this.gatewayService.saveGateway(this.gateway).subscribe(() => {
          this.notification.success(this.translate.instant('Gateway logs requested'), this.COMPONENT_NAME, false);
          this.logStatus();
        }, (error) => {
          this.notification.error(this.translate.instant('Gateway log request failed'), error, this.COMPONENT_NAME, true);
          this.logStatus();
        });
      }
    }, (error) => {
      this.notification.error(this.translate.instant('Gateway log request failed'), error, this.COMPONENT_NAME, true);
    });
  }

  getUpdateGatewayButtonText(updateStatus?: UpdateStatus): string {
    if (!updateStatus || !updateStatus.state) {
      return 'Update';
    }
    const state = updateStatus.state;
    switch (state) {
      case UPDATE_STATE.PROGRESS:
        return 'Update in progress';
      case UPDATE_STATE.REQUESTED:
        return 'Update requested';
      case UPDATE_STATE.PENDING:
        return 'Update pending';
      case UPDATE_STATE.NONE:
      case UPDATE_STATE.SUCCESS:
      case UPDATE_STATE.AVAILABLE:
      case UPDATE_STATE.ERROR:
        return 'Update';
      default:
        return '';
    }
  }

  getRebootButtonText(gateway: Gateway) {
    const rebootRequest: boolean = gateway.rebootRequest;
    const rebootStatus: RebootStatus | undefined = gateway.rebootStatus;
    if (rebootRequest && rebootStatus?.state === REBOOT_STATE.PROGRESS) {
      return 'Reboot in progress';
    } else if (rebootRequest) {
      return 'Reboot requested';
    } else {
      return 'Reboot';
    }
  }

  getRequestLogsButtonText(gateway: Gateway) {
    const logUploadRequested: boolean = gateway.logUploadRequested;
    const logUploadStatus: LogUploadStatus | undefined = gateway.logUploadStatus;
    if (logUploadRequested && logUploadStatus?.state === LOG_UPLOAD_STATE.PROGRESS) {
      return 'Log request in progress';
    } else if (logUploadRequested) {
      return 'Logs requested';
    } else {
      return 'Request';
    }
  }

  showUpdateGatewaySoftware() {
    return this.gateway.updateStatus && (this.gateway.updateStatus.state === this.updateState.PROGRESS || this.gateway.updateStatus.state === this.updateState.PENDING);
  }

  public onGatewayCommunication() {
    if (!this.isGenericGateway && !this.isArgosGateway) {//available only for edge, generic and argos gateway
      return;
    }

    const modalRef = this.modalService.open(GatewayCommunicationModalComponent, {size: 'lg'});
    modalRef.componentInstance.gateway = this.gateway;
    modalRef.result.then((_result) => {
      this.notification.success(this.translate.instant('Gateway command/configuration send'), this.COMPONENT_NAME, false);
    }, (reason) => {
      this.gatewayCommandOrConfigurationSent = false;
      console.log(`Closed with reason: ${reason}`);
    });
  }

  private handleCPOneStatuses() {
    if (!this.isCPOneGateway) {//available only for CPONE gateway
      return;
    }

    if (this.gateway.updateStatus) {
      if ((Date.now() - +this.gateway.updateStatus.nextUpdate) < (this.POLL_TIMEOUT * this.REQUEST_COUNT)) {
        this.updateStatus();
      } else {
        this.gateway.updateStatus.state = UPDATE_STATE.NONE;
      }
    }
    if (this.gateway.configUploadRequested) {
      this.uploadConfigStatus();
    }
    if (this.gateway.configDownloadRequested) {
      this.downloadConfigStatus();
    }
    if (this.gateway.rebootRequest) {
      this.rebootStatus();
    }
    if (this.gateway.logUploadRequested) {
      this.logStatus();
    }
  }

  private updateStatus() {
    const subscription: Subscription = this.gatewayDetails(this.REQUEST_COUNT).subscribe((gateway) => {
      this.gateway.updateStatus = gateway.updateStatus;
      if (this.gateway.updateStatus?.state !== UPDATE_STATE.PENDING) {
        subscription.unsubscribe();
      }
    }, (error) => {
      console.log('GatewayFunctions. updateStatus. Error getting gateway.', error);
      subscription.unsubscribe();
    }, () => {
      if (this.gateway.updateStatus) {
        this.gateway.updateStatus.state = UPDATE_STATE.NONE;
      }
    });
  }

  private uploadConfigStatus() {
    const subscription: Subscription = this.gatewayDetails(this.REQUEST_COUNT).subscribe((gateway) => {
      this.gateway.configUploadRequested = gateway.configUploadRequested;
      this.gateway.configLastUploadTimestamp = gateway.configLastUploadTimestamp;
      if (!this.gateway.configUploadRequested) {
        subscription.unsubscribe();
      }
    }, (error) => {
      console.log('GatewayFunctions. uploadConfigStatus. Error getting gateway.', error);
      subscription.unsubscribe();
    }, () => {
      this.gateway.configUploadRequested = false;
      this.gateway.configLastUploadTimestamp = -1;
      this.saveGateway();
    });
  }

  private downloadConfigStatus() {
    const subscription: Subscription = this.gatewayDetails(this.REQUEST_COUNT).subscribe((gateway) => {
      this.gateway.configDownloadRequested = gateway.configDownloadRequested;
      this.gateway.configPath = gateway.configPath;
      if (!this.gateway.configDownloadRequested) {
        subscription.unsubscribe();
      }
    }, (error) => {
      console.log('GatewayFunctions. downloadConfigStatus. Error getting gateway.', error);
      subscription.unsubscribe();
    }, () => {
      this.gateway.configDownloadRequested = false;
      this.gateway.configPath = '';
      this.saveGateway();
    });
  }

  private rebootStatus() {
    const subscription: Subscription = this.gatewayDetails(this.REQUEST_COUNT).subscribe((gateway) => {
      this.gateway.rebootRequest = gateway.rebootRequest;
      this.gateway.rebootStatus = gateway.rebootStatus;
      if (!this.gateway.rebootRequest) {
        subscription.unsubscribe();
      }
    }, (error) => {
      console.log('GatewayFunctions. rebootStatus. Error getting gateway.', error);
      subscription.unsubscribe();
    }, () => {
      this.gateway.rebootRequest = false;
      if (this.gateway.rebootStatus) {
        this.gateway.rebootStatus.state = REBOOT_STATE.NONE;
      }
      this.saveGateway();
    });
  }

  private logStatus() {
    const subscription: Subscription = this.gatewayDetails(this.REQUEST_COUNT).subscribe((gateway) => {
      this.gateway.logUploadRequested = gateway.logUploadRequested;
      this.gateway.logUploadStatus = gateway.logUploadStatus;
      if (!this.gateway.logUploadRequested) {
        subscription.unsubscribe();
      }
    }, (error) => {
      console.log('GatewayFunctions.logStatus. Error getting gateway.', error);
      subscription.unsubscribe();
    }, () => {
      this.gateway.logUploadRequested = false;
      if (this.gateway.logUploadStatus) {
        this.gateway.logUploadStatus.state = LOG_UPLOAD_STATE.NONE;
      }
      this.saveGateway();
    });
  }

  private gatewayDetails(requestCount): Observable<Gateway> {
    return interval(this.POLL_TIMEOUT)
    .pipe(take(requestCount), switchMap(() => this.gatewayService.getGatewayDetails(this.gateway.id)));
  }

  private saveGateway(): void {
    this.notification.warning(this.translate.instant('Gateway not reachable, stop request'), this.COMPONENT_NAME);
    this.gatewayService.saveGateway(this.gateway).pipe(take(1)).subscribe(() => {
      this.notification.success(this.translate.instant('Gateway request stopped'), this.COMPONENT_NAME, false);
    }, error => {
      this.notification.error(this.translate.instant('Gateway request could not be stopped'), error, this.COMPONENT_NAME, true);
    });
  }

  private setLoggedUserDetails() {
    this.loadingIndicator.show();
    this.authenticationService.getLoggedUserDetails().pipe(takeUntil(this.destroy$)).subscribe((user: User) => {
      this.rootMachineryGroupId = user.role === USER_ROLE.SUPERADMIN ? -1 : user.machineGroup;
      this.rootOrganizationId = user.role === USER_ROLE.SUPERADMIN ? -1 : user.parentId;
      this.whitelistedMachineryGroups = user.role === USER_ROLE.SUPERADMIN ? [] : user.getWhiteListedMachineryGroups(true);
      this.userDetailsLoaded = true;
      this.loadingIndicator.hide();
    }, () => {
      this.loadingIndicator.hide();
      console.error('Error getting user details');
    });


  }
}
